/**
 * <recently-viewed-products> - the visitor's recently viewed products (sections/recently-viewed.liquid).
 *
 * Storage: product handles only, newest first, deduplicated, at most MAX_HISTORY, in localStorage. Every storage
 * access is guarded - private mode or a full quota just means no history.
 *
 * Rendering: each handle is rendered by Shopify through the documented Section Rendering API in the context of that
 * product's URL ({root}products/{handle}?section_id=recently-viewed-item), which returns one locked Product Card. The
 * cards' own custom elements upgrade on insertion like anywhere else; browsers never execute <script> elements
 * inserted through innerHTML, and the only scripts in a card are its JSON data islands.
 *
 *   - Loads only when the section nears the viewport; requests are shared between instances (one per handle per
 *     page) and aborted if every requester leaves the page (Theme Editor re-render, navigation).
 *   - The product being viewed is recorded, then excluded from the list.
 *   - A handle that no longer renders a product (deleted / unpublished) is removed from the history.
 *   - Layout: cards are wrapped in the class named by the list's data-item-class ("section-grid__item" for the
 *     grid, "carousel__item" for the slider track inside <es-carousel>, which updates its own arrows on resize).
 */
const RECENTLY_VIEWED_KEY = 'es:recently-viewed';
const RECENTLY_VIEWED_MAX_HISTORY = 12;
const recentlyViewedRequests = new Map();

const RecentlyViewedStore = {
  read() {
    try {
      const value = JSON.parse(window.localStorage.getItem(RECENTLY_VIEWED_KEY) || '[]');
      return Array.isArray(value) ? value.filter((handle) => typeof handle === 'string' && handle.length > 0 && handle.length <= 255) : [];
    } catch (error) {
      return [];
    }
  },

  write(handles) {
    try {
      window.localStorage.setItem(RECENTLY_VIEWED_KEY, JSON.stringify(handles.slice(0, RECENTLY_VIEWED_MAX_HISTORY)));
    } catch (error) {
      // Storage unavailable: the history is simply not kept.
    }
  },

  record(handle) {
    if (!handle) return;
    this.write([handle, ...this.read().filter((item) => item !== handle)]);
  },

  remove(handle) {
    this.write(this.read().filter((item) => item !== handle));
  },
};

class RecentlyViewedProducts extends HTMLElement {
  constructor() {
    super();
    this.onIntersect = this.onIntersect.bind(this);
    this.observer = null;
    this.controller = null;
    this.requested = false;
  }

  connectedCallback() {
    this.current = this.dataset.current || '';
    this.limit = parseInt(this.dataset.limit, 10) || 4;
    const root = this.dataset.root || '/';
    this.root = root.endsWith('/') ? root : `${root}/`;

    if (this.current) RecentlyViewedStore.record(this.current);
    if (this.requested) return;

    if (typeof IntersectionObserver === 'undefined') {
      this.load();
      return;
    }
    this.observer = new IntersectionObserver(this.onIntersect, { rootMargin: '0px 0px 400px 0px' });
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.observer = null;
    this.controller?.abort();
    this.controller = null;
  }

  onIntersect(entries) {
    if (!entries.some((entry) => entry.isIntersecting)) return;
    this.observer?.disconnect();
    this.observer = null;
    this.load();
  }

  fetchCard(handle, signal) {
    if (!recentlyViewedRequests.has(handle)) {
      const url = `${this.root}products/${encodeURIComponent(handle)}?section_id=recently-viewed-item`;
      const request = fetch(url, { signal })
        .then((response) => (response.ok ? response.text() : ''))
        .then((text) => {
          const item = new DOMParser().parseFromString(text, 'text/html').querySelector('[data-recently-viewed-item]');
          return item ? item.innerHTML : null;
        })
        .catch((error) => {
          recentlyViewedRequests.delete(handle);
          throw error;
        });
      recentlyViewedRequests.set(handle, request);
    }
    return recentlyViewedRequests.get(handle);
  }

  async load() {
    if (this.requested) return;
    this.requested = true;

    const handles = RecentlyViewedStore.read().filter((handle) => handle !== this.current).slice(0, this.limit);
    if (handles.length === 0) {
      this.showEmpty();
      return;
    }

    this.controller = new AbortController();
    const results = await Promise.allSettled(handles.map((handle) => this.fetchCard(handle, this.controller.signal)));
    if (!this.isConnected) return;
    this.controller = null;

    const list = this.querySelector('[data-recently-viewed-list]');
    /* Grid or slider: the section names the item class its layout expects. */
    const itemClass = list?.dataset.itemClass || 'section-grid__item';
    const fragment = document.createDocumentFragment();
    results.forEach((result, index) => {
      if (result.status === 'fulfilled' && result.value) {
        const item = document.createElement('div');
        item.className = itemClass;
        item.innerHTML = result.value;
        fragment.append(item);
      } else if (result.status === 'fulfilled') {
        RecentlyViewedStore.remove(handles[index]);
      }
    });

    if (!list || fragment.childNodes.length === 0) {
      this.showEmpty();
      return;
    }

    list.replaceChildren(fragment);
    this.querySelector('[data-recently-viewed-content]')?.removeAttribute('hidden');
    this.querySelector('[data-recently-viewed-hint]')?.remove();
  }

  showEmpty() {
    if (window.Shopify?.designMode) return;
    this.closest('.shopify-section')?.setAttribute('hidden', '');
  }
}

if (!customElements.get('recently-viewed-products')) {
  customElements.define('recently-viewed-products', RecentlyViewedProducts);
}
